import { useLocation, Link } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { ClipboardList, CalendarDays, LayoutDashboard, Columns3, Users, Settings, LogOut, Droplets, Lock, PenTool, Truck, PackageCheck, Bug, TriangleAlert, ShieldCheck } from "lucide-react";
import { MeshScreenIcon, PrintingPressIcon } from "@/lib/milestone-icons";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { TeamMember } from "@shared/schema";
import { SPECIALTIES } from "@shared/schema";
import { useUser } from "@/lib/user-context";
import { ChangePasswordDialog } from "@/components/change-password-dialog";
import BugReportDialog from "@/components/bug-report-dialog";
import { ThemeToggle } from "@/components/theme-toggle";
import productionHubLogo from "@assets/Production_Hub_1771339483036.png";

interface NavItem {
  title: string;
  url: string;
  icon: any;
  adminOnly: boolean;
  superAdminOnly?: boolean;
  allowedSpecialties?: string[];
}

const navItems: NavItem[] = [
  { title: "Dashboard", url: "/", icon: LayoutDashboard, adminOnly: false },
  { title: "Work Orders", url: "/work-orders", icon: ClipboardList, adminOnly: false },
  { title: "Schedule", url: "/schedule", icon: CalendarDays, adminOnly: false, allowedSpecialties: ["press_operator", "embroidery_operator", "heat_press_operator", "polybag_operator"] },
  { title: "Lanes", url: "/lanes", icon: Columns3, adminOnly: false, allowedSpecialties: ["press_operator", "embroidery_operator", "heat_press_operator", "polybag_operator"] },
  { title: "ScreenMaster", url: "/screenmaster", icon: MeshScreenIcon as any, adminOnly: false, allowedSpecialties: ["screen_burner"] },
  { title: "InkMaster", url: "/inkmaster", icon: Droplets, adminOnly: false, allowedSpecialties: ["ink_mixer"] },
  { title: "Digitizing", url: "/digitizing", icon: PenTool, adminOnly: false, allowedSpecialties: ["digitizer"] },
  { title: "Receiving", url: "/receiving", icon: Truck, adminOnly: false, allowedSpecialties: ["product_handler", "order_management"] },
  { title: "Packing", url: "/packing", icon: PackageCheck, adminOnly: false, allowedSpecialties: ["product_handler", "quality_control"] },
  { title: "Duffs", url: "/duffs", icon: TriangleAlert, adminOnly: false, allowedSpecialties: [] },
  { title: "QC Saves", url: "/qc-saves", icon: ShieldCheck, adminOnly: false },
  { title: "Machines", url: "/machines", icon: PrintingPressIcon as any, adminOnly: true },
  { title: "Team", url: "/team", icon: Users, adminOnly: true },
  { title: "Settings", url: "/settings", icon: Settings, adminOnly: true },
];

function isNavItemVisible(
  item: NavItem,
  isAdmin: boolean,
  isManager: boolean,
  isSuperAdmin: boolean,
  specialties: string[],
  allSpecialties: string[],
): boolean {
  // Super-admin-only pages (Dev Recycle Bin): super admins only
  if (item.superAdminOnly) return isSuperAdmin;

  // Admin-only pages (Machines, Team, Settings): admins and super admins only
  if (item.adminOnly) return isAdmin || isSuperAdmin;

  // No specialty restriction (Dashboard, Work Orders): everyone sees these
  if (!item.allowedSpecialties) return true;

  // Super admins see everything
  if (isSuperAdmin) return true;

  // Admins see everything
  if (isAdmin) return true;

  // Manager-tier pages: only managers see Schedule and Lanes.
  const MANAGER_ONLY_PAGES = ["/schedule", "/lanes"];
  if (MANAGER_ONLY_PAGES.includes(item.url)) {
    return isManager; // team members never see these
  }

  // For specialty pages (ScreenMaster, InkMaster, Digitizing):
  // Managers check own + managed specialties
  // Team members check own specialties only
  const checkSpecialties = isManager ? allSpecialties : specialties;
  return checkSpecialties.some(s => item.allowedSpecialties!.includes(s));
}

function getSpecialtyLabels(specialties: string[]): string {
  return specialties
    .map(s => SPECIALTIES.find(sp => sp.value === s)?.label)
    .filter(Boolean)
    .join(" · ");
}

export { navItems, isNavItemVisible };

export function AppSidebar() {
  const [location] = useLocation();
  const { displayName, isAdmin, isManager, isSuperAdmin, specialties, allSpecialties, logout, isLoggingOut, user, isImpersonating } = useUser();

  const [changePwOpen, setChangePwOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);

  const { data: teamMembers } = useQuery<TeamMember[]>({
    queryKey: ["/api/team-members"],
  });

  const currentMember = teamMembers?.find(
    (m) => m.id === user?.teamMemberId || m.displayName === displayName
  );

  const specialtyLabel = !isAdmin && !isManager && specialties.length > 0
    ? getSpecialtyLabels(specialties)
    : null;

  return (
    <Sidebar className="pp-hub-sidebar hidden md:flex">
      <SidebarHeader className="p-0">
        <div className="flex items-center justify-center px-4 py-4">
          <img src={productionHubLogo} alt="Production Hub" className="w-full max-w-[11.5rem]" />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="p-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-1 px-2.5">
              {navItems.filter((item) => isNavItemVisible(item, isAdmin, isManager, isSuperAdmin, specialties, allSpecialties)).map((item) => {
                const isActive =
                  item.url === "/"
                    ? location === "/"
                    : location.startsWith(item.url);
                return (
                  <SidebarMenuItem key={item.title} className="px-0">
                    <SidebarMenuButton
                      asChild
                      data-active={isActive}
                      className={`rounded-lg py-3 h-auto text-[0.85rem] sidebar-menu-item ${isActive ? "font-bold sidebar-active" : "font-medium"}`}
                    >
                      <Link href={item.url} aria-current={isActive ? "page" : undefined} data-testid={`nav-${item.title.toLowerCase().replace(/\s/g, "-")}`}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-0">
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <ThemeToggle />
        </div>
        <button
          onClick={() => setBugReportOpen(true)}
          className="flex items-center gap-2 px-3 py-2 w-full text-left text-white/70 hover:text-white transition-colors"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
          data-testid="button-bug-report"
        >
          <Bug className="h-4 w-4 shrink-0" />
          <span className="text-[13px] font-medium">Report a Bug</span>
        </button>
        <BugReportDialog open={bugReportOpen} onOpenChange={setBugReportOpen} />
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="flex items-center gap-1.5 pl-2.5 pr-2 py-2.5 w-full text-left cursor-pointer hover:brightness-110 transition-all"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.10)" }}
              data-testid="button-user-profile"
            >
              <Avatar className="h-6 w-6 shrink-0">
                {currentMember?.profilePicPath ? (
                  <AvatarImage src={currentMember.profilePicPath} alt={displayName} />
                ) : null}
                <AvatarFallback className="text-[10px] font-bold" style={{ backgroundColor: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}>
                  {displayName?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <span className="text-[13px] text-white/90 font-medium truncate block">{displayName || "Unknown"}</span>
                {!isImpersonating && specialtyLabel && (
                  <span className="text-[10px] text-white/50 truncate block">{specialtyLabel}</span>
                )}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-56 p-3" data-testid="popover-user-profile">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="h-10 w-10 shrink-0">
                {currentMember?.profilePicPath ? (
                  <AvatarImage src={currentMember.profilePicPath} alt={displayName} />
                ) : null}
                <AvatarFallback className="text-sm font-bold" style={{ backgroundColor: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}>
                  {displayName?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" data-testid="text-profile-name">{displayName}</p>
                <p className="text-xs text-muted-foreground" data-testid="text-profile-username">@{user?.username}</p>
              </div>
            </div>
            <div className="border-t pt-2 space-y-0.5">
              <button
                onClick={() => setChangePwOpen(true)}
                className="flex items-center gap-2 w-full text-left text-sm py-1.5 px-1 rounded hover:bg-muted transition-colors"
                data-testid="button-change-password-sidebar"
              >
                <Lock className="h-4 w-4" />
                <span>Change Password</span>
              </button>
              <button
                onClick={logout}
                disabled={isLoggingOut}
                aria-busy={isLoggingOut}
                className="flex items-center gap-2 w-full text-left text-sm text-red-600 hover:text-red-700 py-1.5 px-1 rounded hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                data-testid="button-logout"
              >
                <LogOut className="h-4 w-4" />
                <span>{isLoggingOut ? "Signing out…" : "Sign out"}</span>
              </button>
            </div>
          </PopoverContent>
          <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
        </Popover>
      </SidebarFooter>
    </Sidebar>
  );
}
